import { Button, Callout, Field } from '@qr/ui';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { useEffect, useState, type FormEvent } from 'react';
import { api, messageFromError } from '../lib/api.js';
import { db } from '../lib/firebase.js';
import { useConfirmar } from '../lib/dialogos.js';

/**
 * Carpetas para agrupar códigos.
 *
 * Con más de treinta códigos la lista plana deja de servir. Una carpeta es
 * solo un nombre: los códigos guardan a cuál pertenecen y la lista filtra por
 * eso. Sin jerarquía —nada de carpetas dentro de carpetas—, porque casi nadie
 * las usa y complican cada consulta para siempre.
 */

export interface Carpeta {
  readonly id: string;
  readonly name: string;
}

/** Escucha las carpetas del espacio abierto. */
export function useCarpetas(orgId: string | null): Carpeta[] {
  const [carpetas, setCarpetas] = useState<Carpeta[]>([]);

  // Al cambiar de espacio se vacía durante el render, no en el efecto: con un
  // efecto React llegaría a pintar una vez las carpetas del espacio anterior
  // bajo el nombre del nuevo.
  const [orgVisto, setOrgVisto] = useState(orgId);
  if (orgId !== orgVisto) {
    setOrgVisto(orgId);
    setCarpetas([]);
  }

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(
      query(collection(db, `orgs/${orgId}/folders`), orderBy('name')),
      (snap) => {
        setCarpetas(snap.docs.map((d) => ({ id: d.id, name: d.get('name') as string })));
      },
      (error) => {
        // Sin carpetas la lista sigue funcionando entera, así que esto no se
        // le cuenta a nadie; queda en la consola por si hay que mirarlo.
        console.error('No se pudieron cargar las carpetas', error);
        setCarpetas([]);
      },
    );
  }, [orgId]);

  return carpetas;
}

export function GestorDeCarpetas({
  orgId,
  carpetas,
  puedeBorrar,
}: {
  readonly orgId: string | null;
  readonly carpetas: readonly Carpeta[];
  readonly puedeBorrar: boolean;
}) {
  const [nombre, setNombre] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  async function crear(event: FormEvent) {
    event.preventDefault();
    if (!orgId || nombre.trim() === '') return;
    setError(null);
    setAviso(null);
    setBusy(true);
    try {
      await api.createFolder({ orgId, name: nombre.trim() });
      setNombre('');
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <form onSubmit={crear} className="stack-sm" noValidate>
        <Field
          label="Nombre de la carpeta"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Sucursal Chapinero"
          maxLength={60}
          required
          error={error}
        />
        <div>
          <Button type="submit" loading={busy} disabled={nombre.trim() === ''}>
            Crear carpeta
          </Button>
        </div>
      </form>

      {aviso ? <Callout tone="success">{aviso}</Callout> : null}

      {carpetas.length > 0 ? (
        <ul className="flex list-none flex-col gap-2 border-t border-line p-0 pt-4">
          {carpetas.map((c) => (
            <Fila
              key={c.id}
              carpeta={c}
              orgId={orgId}
              puedeBorrar={puedeBorrar}
              onAviso={setAviso}
            />
          ))}
        </ul>
      ) : (
        <p className="m-0 text-[13px] text-ink-3">
          Todavía no hay carpetas. Crea la primera arriba y luego asigna códigos
          desde el detalle de cada uno.
        </p>
      )}
    </div>
  );
}

function Fila({
  carpeta,
  orgId,
  puedeBorrar,
  onAviso,
}: {
  readonly carpeta: Carpeta;
  readonly orgId: string | null;
  readonly puedeBorrar: boolean;
  readonly onAviso: (texto: string) => void;
}) {
  const confirmar = useConfirmar();
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(carpeta.name);
  const [busy, setBusy] = useState(false);

  return (
    <li className="flex flex-wrap items-center gap-2 rounded-sm border border-line px-3 py-2">
      {editando ? (
        <input
          id={`carpeta-${carpeta.id}`}
          className="qr-field__input flex-1"
          value={nombre}
          maxLength={60}
          onChange={(e) => setNombre(e.target.value)}
        />
      ) : (
        <span className="flex-1 truncate text-[13.5px]">{carpeta.name}</span>
      )}

      {editando ? (
        <>
          <Button
            size="sm"
            loading={busy}
            disabled={nombre.trim() === '' || nombre === carpeta.name}
            onClick={async () => {
              if (!orgId) return;
              setBusy(true);
              try {
                await api.renameFolder({
                  orgId,
                  folderId: carpeta.id,
                  name: nombre.trim(),
                });
                setEditando(false);
              } finally {
                setBusy(false);
              }
            }}
          >
            Guardar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setNombre(carpeta.name);
              setEditando(false);
            }}
          >
            Cancelar
          </Button>
        </>
      ) : (
        <>
          <Button size="sm" variant="secondary" onClick={() => setEditando(true)}>
            Renombrar
          </Button>
          {puedeBorrar ? (
            <Button
              size="sm"
              variant="ghost"
              loading={busy}
              onClick={async () => {
                if (!orgId) return;
                const ok = await confirmar({
                  titulo: `Borrar la carpeta "${carpeta.name}"`,
                  mensaje:
                    'Los códigos que haya dentro NO se borran: se quedan sin carpeta.',
                  aceptar: 'Borrar la carpeta',
                  grave: true,
                });
                if (!ok) return;
                setBusy(true);
                try {
                  const r = await api.deleteFolder({ orgId, folderId: carpeta.id });
                  onAviso(
                    r.codigosLiberados > 0
                      ? `Carpeta borrada. ${r.codigosLiberados} código${r.codigosLiberados === 1 ? '' : 's'} se quedó sin carpeta y sigue funcionando.`
                      : 'Carpeta borrada.',
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              Borrar
            </Button>
          ) : null}
        </>
      )}
    </li>
  );
}
